/* eslint-disable react/no-unknown-property */
/* ============================================================
   SpaceshipMesh – GREINCH orbital shuttle
   - Lathe hull with brushed-metal panels, dorsal spine, belly
     heat-shield tiles, framed canopy
   - Swept delta wings (extruded, bevelled) with leading-edge
     glow strip, canted tip fins, nav lights
   - Three engine bells with animated plasma cores
   - RCS thruster blocks, sensor mast, docking collar
   Nose -Z, tail +Z. Twin engines sit near (±2.9, -0.6, 1.7) so
   the exhaust trail emitters in Airplane() line up.
   ============================================================ */
import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { getBrushedMetalTexture } from "@/components/world/textures";

const HULL_GEO = (() => {
  const zNose = -5.2;
  const zTail = 3.6;
  const N = 48;
  const pts = [];
  for (let i = 0; i <= N; i++) {
    const z = zNose + (i / N) * (zTail - zNose);
    let r;
    if (z < -2.2) {
      const s = Math.min(1, Math.max(0, (-2.2 - z) / 3.0));
      r = 0.95 * Math.pow(1 - s * s, 0.62);
    } else if (z < 2.0) {
      r = 0.95 + Math.sin(((z + 2.2) / 4.2) * Math.PI) * 0.08;
    } else {
      const s = Math.min(1, Math.max(0, (z - 2.0) / 1.6));
      r = 0.95 - s * 0.25;
    }
    pts.push(new THREE.Vector2(Math.max(0.02, r), z));
  }
  const geo = new THREE.LatheGeometry(pts, 36);
  geo.rotateX(Math.PI / 2);
  // flatten the belly slightly (lifting-body look)
  const pos = geo.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    const y = pos.getY(i);
    if (y < 0) pos.setY(i, y * 0.78);
  }
  geo.computeVertexNormals();
  return geo;
})();

const WING_GEO = (() => {
  const s = new THREE.Shape();
  // x = span, y = chord (aft positive)
  s.moveTo(0, -2.6);
  s.lineTo(5.4, 1.4);
  s.lineTo(5.6, 2.3);
  s.lineTo(2.2, 2.7);
  s.lineTo(0, 2.9);
  s.lineTo(0, -2.6);
  const geo = new THREE.ExtrudeGeometry(s, { depth: 0.18, bevelEnabled: true, bevelThickness: 0.06, bevelSize: 0.08, bevelSegments: 2 });
  geo.rotateX(Math.PI / 2);
  geo.translate(0, 0.09, 0);
  geo.computeVertexNormals();
  return geo;
})();

function finGeometry(rootChord, tipChord, height, sweep, thickness) {
  const s = new THREE.Shape();
  s.moveTo(0, 0);
  s.lineTo(rootChord, 0);
  s.lineTo(sweep + tipChord, height);
  s.lineTo(sweep, height);
  s.lineTo(0, 0);
  const geo = new THREE.ExtrudeGeometry(s, { depth: thickness, bevelEnabled: true, bevelThickness: 0.02, bevelSize: 0.03, bevelSegments: 1 });
  geo.rotateY(-Math.PI / 2);
  geo.translate(thickness / 2, 0, 0);
  geo.computeVertexNormals();
  return geo;
}
const TIP_FIN_GEO = finGeometry(1.6, 0.6, 1.3, 0.9, 0.12);
const DORSAL_FIN_GEO = finGeometry(2.2, 0.7, 1.7, 1.3, 0.16);

const BELL_GEO = (() => {
  const pts = [
    [0.3, 0],
    [0.34, 0.25],
    [0.45, 0.7],
    [0.62, 1.15],
    [0.7, 1.3],
  ].map(([r, z]) => new THREE.Vector2(r, z));
  const g = new THREE.LatheGeometry(pts, 24);
  g.rotateX(Math.PI / 2); // opens toward +z
  g.computeVertexNormals();
  return g;
})();

const MAT = {
  wing: new THREE.MeshStandardMaterial({ color: "#C9C3B8", metalness: 0.5, roughness: 0.4, emissive: "#2A2A30", emissiveIntensity: 0.35 }),
  dark: new THREE.MeshStandardMaterial({ color: "#2E323A", metalness: 0.6, roughness: 0.45, emissive: "#101216", emissiveIntensity: 0.4 }),
  tiles: new THREE.MeshStandardMaterial({ color: "#1E1C22", metalness: 0.2, roughness: 0.95, emissive: "#0A090C", emissiveIntensity: 0.4 }),
  accent: new THREE.MeshStandardMaterial({ color: "#6B1F47", metalness: 0.5, roughness: 0.4, emissive: "#6B1F47", emissiveIntensity: 0.35 }),
  coral: new THREE.MeshStandardMaterial({ color: "#E94B4B", emissive: "#E94B4B", emissiveIntensity: 0.8 }),
  glass: new THREE.MeshStandardMaterial({ color: "#0F1A26", metalness: 0.6, roughness: 0.05, emissive: "#8AC5FF", emissiveIntensity: 0.45, transparent: true, opacity: 0.92 }),
  frame: new THREE.MeshStandardMaterial({ color: "#D8D2C4", metalness: 0.6, roughness: 0.3, emissive: "#33322F", emissiveIntensity: 0.35 }),
  bell: new THREE.MeshStandardMaterial({ color: "#5A5F6B", metalness: 0.8, roughness: 0.3, emissive: "#2A2C33", emissiveIntensity: 0.4, side: THREE.DoubleSide }),
  bellInner: new THREE.MeshBasicMaterial({ color: "#C5A8FF", toneMapped: false, side: THREE.BackSide }),
  glowBlue: new THREE.MeshBasicMaterial({ color: "#8AC5FF", toneMapped: false }),
  navRed: new THREE.MeshBasicMaterial({ color: "#FF3B3B", toneMapped: false }),
  navGreen: new THREE.MeshBasicMaterial({ color: "#3BFF7A", toneMapped: false }),
};

function Engine({ position, glowRef, big = false }) {
  const s = big ? 1.3 : 1;
  return (
    <group position={position} scale={s}>
      <mesh material={MAT.dark} position={[0, 0, -0.5]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.42, 0.36, 1.0, 16]} />
      </mesh>
      <mesh geometry={BELL_GEO} material={MAT.bell} />
      <mesh geometry={BELL_GEO} material={MAT.bellInner} scale={[0.96, 0.96, 0.96]} />
      <mesh ref={glowRef} position={[0, 0, 0.6]}>
        <sphereGeometry args={[0.36, 16, 12]} />
        <meshStandardMaterial color={big ? "#C5A8FF" : "#8AC5FF"} emissive={big ? "#C5A8FF" : "#8AC5FF"} emissiveIntensity={1.5} toneMapped={false} />
      </mesh>
    </group>
  );
}

export function SpaceshipMesh({ propRef }) {
  const glowRef = useRef();
  const glowRef2 = useRef();
  const strobeRef = useRef();
  const brushed = useMemo(() => {
    const t = getBrushedMetalTexture("#C9C3B8", 33);
    if (t) t.repeat.set(4, 1);
    return t;
  }, []);
  const hullMat = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        map: brushed || null,
        color: brushed ? "#FFFFFF" : "#C9C3B8",
        metalness: 0.5,
        roughness: 0.36,
        emissive: "#2A2A30",
        emissiveIntensity: 0.4,
      }),
    [brushed]
  );
  useFrame((state) => {
    const t = state.clock.elapsedTime;
    const pulse = 1.2 + Math.sin(t * 8) * 0.4;
    if (glowRef.current) glowRef.current.material.emissiveIntensity = pulse;
    if (glowRef2.current) glowRef2.current.material.emissiveIntensity = 1.2 + Math.sin(t * 8 + 1.5) * 0.4;
    if (strobeRef.current) strobeRef.current.visible = (t * 1.4) % 1 < 0.08;
  });
  return (
    <group scale={0.9}>
      {/* hull */}
      <mesh geometry={HULL_GEO} material={hullMat} />
      {/* belly heat-shield tiles */}
      <mesh material={MAT.tiles} position={[0, -0.62, -0.6]} scale={[1.0, 0.25, 1]}>
        <sphereGeometry args={[0.98, 20, 10, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2]} />
      </mesh>
      <mesh material={MAT.tiles} position={[0, -0.6, -0.6]}>
        <boxGeometry args={[1.5, 0.12, 5.6]} />
      </mesh>
      {/* dorsal spine */}
      <mesh material={MAT.dark} position={[0, 0.85, 0.6]}>
        <boxGeometry args={[0.5, 0.35, 4.6]} />
      </mesh>
      {/* panel seam rings */}
      {[-1.6, 0.4, 1.8].map((z, i) => (
        <mesh key={i} position={[0, 0, z]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[1.0, 0.025, 6, 40]} />
          <meshStandardMaterial color="#3A3A44" metalness={0.7} roughness={0.4} />
        </mesh>
      ))}
      {/* canopy + frame */}
      <mesh material={MAT.glass} position={[0, 0.55, -2.3]} scale={[0.8, 0.62, 1.55]}>
        <sphereGeometry args={[0.8, 24, 16, 0, Math.PI * 2, 0, Math.PI / 2]} />
      </mesh>
      <mesh material={MAT.frame} position={[0, 0.55, -2.3]} rotation={[0, 0, 0]} scale={[0.82, 0.64, 1.57]}>
        <torusGeometry args={[0.8, 0.03, 6, 40, Math.PI]} />
      </mesh>
      <mesh material={MAT.frame} position={[0, 0.55, -2.3]} rotation={[0, Math.PI / 2, 0]} scale={[1.57, 0.64, 0.82]}>
        <torusGeometry args={[0.8, 0.03, 6, 40, Math.PI]} />
      </mesh>
      {/* RCS thruster blocks near the nose */}
      {[
        [0.55, 0.45],
        [-0.55, 0.45],
        [0.55, -0.35],
        [-0.55, -0.35],
      ].map(([x, y], i) => (
        <mesh key={i} material={MAT.dark} position={[x, y, -3.6]}>
          <boxGeometry args={[0.22, 0.16, 0.36]} />
        </mesh>
      ))}
      {/* sensor mast + docking collar */}
      <mesh material={MAT.frame} position={[0, 1.15, -1.2]}>
        <cylinderGeometry args={[0.02, 0.03, 0.6, 6]} />
      </mesh>
      <mesh material={MAT.frame} position={[0, 0, -5.05]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.22, 0.05, 8, 20]} />
      </mesh>
      {/* wings */}
      <group position={[0, -0.15, 0.2]} rotation={[0, 0, 0.05]}>
        <mesh geometry={WING_GEO} material={MAT.wing} />
        <mesh geometry={TIP_FIN_GEO} material={MAT.accent} position={[5.45, 0.1, 1.3]} rotation={[0, 0, -0.35]} />
        <mesh material={MAT.navGreen} position={[5.6, 0.15, 2.0]}>
          <sphereGeometry args={[0.08, 8, 8]} />
        </mesh>
      </group>
      <group position={[0, -0.15, 0.2]} rotation={[0, 0, -0.05]}>
        <mesh geometry={WING_GEO} material={MAT.wing} scale={[-1, 1, 1]} />
        <mesh geometry={TIP_FIN_GEO} material={MAT.accent} position={[-5.45, 0.1, 1.3]} rotation={[0, 0, 0.35]} />
        <mesh material={MAT.navRed} position={[-5.6, 0.15, 2.0]}>
          <sphereGeometry args={[0.08, 8, 8]} />
        </mesh>
      </group>
      {/* leading-edge glow strips */}
      {[1, -1].map((s) => (
        <mesh key={s} material={MAT.glowBlue} position={[s * 2.7, -0.02, -0.4]} rotation={[0, s * -0.64, 0]}>
          <boxGeometry args={[4.9, 0.05, 0.12]} />
        </mesh>
      ))}
      {/* coral livery stripes on the hull */}
      {[0.98, -0.98].map((x) => (
        <mesh key={x} material={MAT.coral} position={[x, 0.1, -0.4]}>
          <boxGeometry args={[0.03, 0.3, 4.6]} />
        </mesh>
      ))}
      {/* dorsal fin */}
      <mesh geometry={DORSAL_FIN_GEO} material={MAT.accent} position={[0, 0.95, 1.3]} />
      <mesh material={MAT.coral} position={[0, 2.2, 2.55]}>
        <boxGeometry args={[0.2, 0.5, 0.5]} />
      </mesh>
      <mesh ref={strobeRef} material={MAT.glowBlue} position={[0, 2.7, 2.6]}>
        <sphereGeometry args={[0.08, 8, 8]} />
      </mesh>
      {/* engines */}
      <Engine position={[2.9, -0.55, 2.4]} glowRef={glowRef} />
      <Engine position={[-2.9, -0.55, 2.4]} glowRef={glowRef2} />
      <Engine position={[0, -0.1, 3.3]} glowRef={propRef} big />
      {/* engine pylons */}
      {[2.9, -2.9].map((x) => (
        <mesh key={x} material={MAT.dark} position={[x * 0.72, -0.5, 2.2]} rotation={[0, 0, x > 0 ? 0.15 : -0.15]}>
          <boxGeometry args={[1.6, 0.3, 1.0]} />
        </mesh>
      ))}
    </group>
  );
}
